"use client";

import Link from "next/link";
import { useState } from "react";
import { ArrowUpRight, CircleDollarSign, Menu, X } from "lucide-react";
import { signOut } from "@/actions/auth";
import { BrandLockup } from "@/components/brand/brand-lockup";
import { cn } from "@/lib/utils";

/**
 * The primary navigation follows the product, not the API catalogue. Sources,
 * Solutions and Tools still exist and are still linked from the footer — they
 * are just no longer what the header promises a first-time visitor.
 *
 * The first two entries are anchors on the homepage: there is no separate
 * "how it works" page yet, and a menu item that dead-ends teaches people not to
 * click again. When those pages exist they replace the anchors.
 */
const navLinks = [
  { href: "/#how-it-works", label: "How it works" },
  { href: "/#who-its-for", label: "Who it's for" },
  { href: "/#pricing", label: "Pricing" },
  { href: "/blog", label: "Blog" },
  { href: "/about", label: "About" },
];

export function NavbarClient({
  isAuthenticated,
  credits,
}: {
  isAuthenticated: boolean;
  credits?: number;
}) {
  const [open, setOpen] = useState(false);

  return (
    <header className="border-line-soft bg-canvas/85 sticky top-0 z-50 w-full border-b py-3 backdrop-blur-md">
      <div className="mx-auto flex max-w-[1240px] items-center justify-between px-5">
        <div className="flex flex-1 items-center">
          <Link
            href="/"
            className="text-ink flex shrink-0 items-center"
            aria-label="SenseCollect home"
          >
            <BrandLockup />
          </Link>
        </div>

        <nav className="hidden max-w-max flex-1 items-center justify-center lg:flex">
          <ul className="flex list-none items-center gap-1">
            {navLinks.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className="text-ink-2 hover:bg-surface-2 hover:text-ink whitespace-nowrap rounded-full px-3 py-2 text-sm transition-colors"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex flex-1 items-center justify-end gap-2">
          {isAuthenticated ? (
            <Link
              href="/dashboard/billing"
              className="border-line text-ink hover:bg-surface-2 hidden h-9 items-center gap-1.5 rounded-full border px-3 font-mono text-[12px] transition-colors sm:inline-flex"
            >
              <CircleDollarSign className="text-ink-3 h-3.5 w-3.5" />
              {credits ?? 0}
              <span className="text-ink-3 hidden sm:inline">credits</span>
            </Link>
          ) : null}

          <Link
            href={isAuthenticated ? "/dashboard" : "/register"}
            className="bg-ink group hidden h-9 items-center gap-2 rounded-full pl-4 pr-1.5 text-sm font-medium text-white transition-transform duration-500 hover:scale-[1.02] active:scale-[0.98] sm:inline-flex"
          >
            <span className="leading-none">
              {isAuthenticated ? "Dashboard" : "Find my leads"}
            </span>
            <span className="bg-signal text-ink inline-flex size-6 shrink-0 items-center justify-center rounded-full transition-transform duration-500 group-hover:translate-x-0.5">
              <ArrowUpRight className="h-3.5 w-3.5" />
            </span>
          </Link>

          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="text-ink hover:bg-surface-2 -mr-1 inline-flex size-9 items-center justify-center rounded-full transition-colors lg:hidden"
          >
            <span className="sr-only">{open ? "Close menu" : "Open menu"}</span>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <div
        className={cn(
          // Anchored to the header rather than the viewport, so the
          // announcement bar above it cannot push the panel out of alignment
          "absolute inset-x-0 top-full z-40 overflow-clip lg:hidden",
          open ? "pointer-events-auto" : "pointer-events-none",
        )}
      >
        <div
          onClick={() => setOpen(false)}
          className={cn(
            "absolute inset-0 h-screen bg-black/50 transition-opacity duration-300",
            open ? "opacity-100" : "opacity-0",
          )}
        />
        <div
          className={cn(
            "bg-canvas border-line-soft relative flex w-full flex-col gap-1 border-b px-5 pb-6 pt-3 transition-transform duration-300 ease-[var(--ease-out-expo)]",
            open ? "translate-y-0" : "-translate-y-[110%]",
          )}
        >
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
              className="text-ink hover:bg-surface-2 rounded-lg px-3 py-2.5 text-[15px] font-medium transition-colors"
            >
              {link.label}
            </Link>
          ))}

          <div className="border-line-soft mt-3 grid gap-2 border-t pt-4">
            {isAuthenticated ? (
              <>
                <Link
                  href="/dashboard"
                  onClick={() => setOpen(false)}
                  className="bg-ink inline-flex h-11 items-center justify-center rounded-full text-sm font-medium text-white"
                >
                  Dashboard
                </Link>
                <form action={signOut}>
                  <button className="border-line text-ink h-11 w-full rounded-full border text-sm font-medium">
                    Sign out
                  </button>
                </form>
              </>
            ) : (
              <>
                <Link
                  href="/register"
                  onClick={() => setOpen(false)}
                  className="bg-ink inline-flex h-11 items-center justify-center rounded-full text-sm font-medium text-white"
                >
                  Find my leads
                </Link>
                <Link
                  href="/login"
                  onClick={() => setOpen(false)}
                  className="border-line text-ink inline-flex h-11 items-center justify-center rounded-full border text-sm font-medium"
                >
                  Log in
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
